import React, { useMemo, useState } from 'react';
import { Modal, Input, Table, Space, Tag, Button, Tooltip } from 'antd';
import { useGetQaqcPipeWelder, useGetPersonnel, useGetCompanies } from '../../hooks/genericHooks';
import type { QaqcPipeWelder, VwPersonnel, Company } from '../../types';

export type WelderSelectProps = {
  open: boolean;
  maxCount?: number;
  initialSelected?: string[];
  onOk: (codes: string[]) => void;
  onCancel: () => void;
  codeInputWidth?: number | string;
  nameInputWidth?: number | string;
  companyInputWidth?: number | string;
};

const WelderSelect: React.FC<WelderSelectProps> = ({ open, maxCount = 5, initialSelected = [], onOk, onCancel, codeInputWidth, nameInputWidth, companyInputWidth }) => {
  const { data: welderResp, isLoading } = useGetQaqcPipeWelder();
  const { data: personnelResp } = useGetPersonnel();
  const { data: companiesResp } = useGetCompanies();
  const all: QaqcPipeWelder[] = (welderResp?.data as unknown as QaqcPipeWelder[]) || [];
  const personnel: VwPersonnel[] = (personnelResp?.data as unknown as VwPersonnel[]) || [];
  const companies: Company[] = (companiesResp?.data as unknown as Company[]) || [];

  const [codeQ, setCodeQ] = useState<string>('');
  const [nameQ, setNameQ] = useState<string>('');
  const [companyQ, setCompanyQ] = useState<string>('');
  const [selected, setSelected] = useState<string[]>(() => (Array.isArray(initialSelected) ? initialSelected.slice(0, maxCount) : []));

  React.useEffect(() => {
    setSelected(Array.isArray(initialSelected) ? initialSelected.slice(0, maxCount) : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const filtered = useMemo(() => {
    const cq = codeQ.trim().toLowerCase();
    const nq = nameQ.trim().toLowerCase();
    const kq = companyQ.trim().toLowerCase();
    return all.filter((w) => {
      const code = (w.welder_name || '').toString().toLowerCase();
      const ownFull = `${w.first_name || ''} ${w.last_name || ''}`.trim().toLowerCase();
      const pers = !ownFull ? personnel.find((p) => p.pers_id === w.personnel_id) : undefined;
      const full = (ownFull || `${pers?.first_name || ''} ${pers?.last_name || ''}`.trim()).toLowerCase();
      const company = (companies.find((c) => c.id === w.company_id)?.name || '').toString().toLowerCase();
      if (cq && !code.includes(cq)) return false;
      if (nq && !full.includes(nq)) return false;
      if (kq && !company.includes(kq)) return false;
      return true;
    });
  }, [all, codeQ, nameQ, companyQ, personnel, companies]);

  const toggle = (code: string) => {
    setSelected((prev) => {
      const exists = prev.includes(code);
      if (exists) return prev.filter((c) => c !== code);
      if (prev.length >= maxCount) return prev; // enforce max
      return [...prev, code];
    });
  };

  return (
    <Modal
      open={open}
      title="Select Welder(s)"
      okText="Ok"
      cancelText="Cancel"
      onOk={() => onOk(selected)}
      onCancel={onCancel}
      destroyOnClose
      footer={[
        <Button key="clear" onClick={() => setSelected([])} danger>
          Unselect all
        </Button>,
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="ok" type="primary" onClick={() => onOk(selected)}>
          Ok
        </Button>,
      ]}
    >
      <Space direction="vertical" style={{ width: '100%' }} size={4}>
        <Space style={{ width: '100%', display: 'flex', flexWrap: 'nowrap', gap: 8 }} wrap={false}>
          <Input
            placeholder="Filter by code"
            allowClear
            value={codeQ}
            onChange={(e) => setCodeQ(e.target.value)}
            style={{ width: codeInputWidth ?? 120 }}
          />
          <Input
            placeholder="Filter by name"
            allowClear
            value={nameQ}
            onChange={(e) => setNameQ(e.target.value)}
            style={{ width: nameInputWidth ?? 180 }}
          />
          <Input
            placeholder="Filter by company"
            allowClear
            value={companyQ}
            onChange={(e) => setCompanyQ(e.target.value)}
            style={{ width: companyInputWidth ?? 160 }}
          />
        </Space>
        <div style={{ fontSize: 12 }}>
          Selected ({selected.length}/{maxCount}): {selected.map((c) => (<Tag key={c}>{c}</Tag>))}
          {selected.length >= maxCount && <span style={{ color: '#faad14', marginLeft: 8 }}>Max reached</span>}
        </div>
        <Table
          size="small"
          bordered
          pagination={false}
          loading={isLoading}
          rowKey={(w: any) => w.welder_name}
          dataSource={filtered}
          columns={[
            { title: 'Code', dataIndex: 'welder_name', key: 'code', width: 80, render: (code: any, w: any) => (
                <Tooltip title={(Array.isArray(w.wps_list) && w.wps_list.length) ? w.wps_list.join(', ') : 'No WPS'}>
                  <span>{code}</span>
                </Tooltip>
              )
            },
            { title: 'Name', key: 'name', render: (_: any, w: any) => {
                const ownFull = `${w.first_name || ''} ${w.last_name || ''}`.trim();
                const pers = !ownFull ? personnel.find((p) => p.pers_id === w.personnel_id) : undefined;
                const full = ownFull || `${pers?.first_name || ''} ${pers?.last_name || ''}`.trim();
                return (
                  <Tooltip title={(Array.isArray(w.wps_list) && w.wps_list.length) ? w.wps_list.join(', ') : 'No WPS'}>
                    <span>{full}</span>
                  </Tooltip>
                );
              }
            },
            { title: 'Company', key: 'company', width: 140, render: (_: any, w: any) => (companies.find((c) => c.id === w.company_id)?.name || w.company_id) },
          ]}
          rowSelection={{
            columnWidth: 36,
            selectedRowKeys: selected,
            onSelect: (record: any, checked: boolean) => {
              const code = record?.welder_name || '';
              if (checked) {
                setSelected((prev) => prev.includes(code) || prev.length >= maxCount ? prev : [...prev, code]);
              } else {
                setSelected((prev) => prev.filter((c) => c !== code));
              }
            },
            onSelectAll: (checked: boolean, selectedRows: any[]) => {
              if (!checked) { setSelected([]); return; }
              const codes = selectedRows.map((r) => r.welder_name).filter(Boolean);
              const limited = codes.slice(0, Math.max(0, maxCount));
              setSelected(limited);
            },
            getCheckboxProps: (record: any) => ({ disabled: !selected.includes(record.welder_name) && selected.length >= maxCount }),
          }}
          onRow={(record: any) => ({
            onClick: () => {
              const code = record?.welder_name || '';
              toggle(code);
            },
            onDoubleClick: () => {
              const code = record?.welder_name || '';
              const exists = selected.includes(code);
              const next = exists ? selected : (selected.length >= maxCount ? selected : [...selected, code]);
              onOk(next);
            },
          })}
          style={{ maxHeight: 360, overflow: 'auto' }}
        />
      </Space>
    </Modal>
  );
};

export default WelderSelect;
